import {CommonModule} from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Injector,
  Input,
  Output,
  signal,
} from '@angular/core';
import {TuiButton, TuiHint, TuiIcon, TuiTitle} from '@taiga-ui/core';
import type {TuiHorizontalDirection} from '@taiga-ui/core/types';
import {TuiDrawer} from '@taiga-ui/kit';
import {TuiHeader} from '@taiga-ui/layout/components/header';

@Component({
  selector: 'gatewee-drawer',
  standalone: true,
  imports: [
    CommonModule,
    TuiDrawer,
    TuiButton,
    TuiHint,
    TuiIcon,
    TuiTitle,
    TuiHeader,
  ],
  templateUrl: './gatewee-drawer.component.html',
  styleUrls: ['./gatewee-drawer.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GateweeDrawerComponent {
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly injector = inject(Injector);

  /** Top hairline above the footer slot; only when a `<footer>` is actually projected. */
  protected readonly hasFooterSlot = signal(false);

  @Input() open = false;

  @Output() readonly openChange = new EventEmitter<boolean>();

  @Input() title = '';

  @Input() subtitle = '';

  /**
   * When false, the header does not render `title` / `subtitle` (only the close
   * control). Use this to put headings inside projected content (e.g. dynamic
   * titles next to the form).
   */
  @Input() showHeaderTitle = true;

  @Input() direction: TuiHorizontalDirection = 'right';

  @Input() overlay = true;

  @Input() closeOnBackdrop = true;

  @Input() closeButtonLabel = 'Fechar';

  @Input() closeIcon = '@tui.x';

  /** When true, shows an icon-only back control before the title (label via tooltip). */
  @Input() showBackButton = false;

  /** Tooltip + aria-label for the icon-only back control (pass translated string). */
  @Input() backButtonLabel = '';

  @Output() readonly backClick = new EventEmitter<void>();

  close(): void {
    if (!this.open) {
      return;
    }
    this.openChange.emit(false);
  }

  onBackdropClick(): void {
    if (this.closeOnBackdrop) {
      this.close();
    }
  }

  onBackClick(event: Event): void {
    event.stopPropagation();
    this.backClick.emit();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.open) {
      this.close();
    }
  }
}
